import { OptionButton } from "@/react/components/OptionButton";
import {
  useChainInfo,
  useBalanceAsync,
  useTransferAsync,
  useTokenBalanceAsync,
  // useWriteContractAsync,
  useAccount,
  getProvider,
} from "@/react/hooks";
import { Spinner } from "@/react/icons";
import { WithdrawLogo } from "based-assets/index";
import { NetworkSymbols } from "based-auth/networks";
import {
  FallbackProvider,
  formatUnits,
  JsonRpcProvider,
  parseUnits,
} from "ethers";
import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { useSimpleKitContext } from "../context/SimpleKitContext";

export function WithdrawFunds() {
  const amountRef = useRef<HTMLInputElement>(null);
  const tokenRef = useRef<HTMLInputElement>(null);

  const [type, setType] = useState<string>("native");
  const { nativeSymbol, nativeDecimals } = useChainInfo();
  const { chainId, address, network } = useAccount();
  const { chains, wagmiConfig } = useSimpleKitContext();

  const { mutateAsync: fetchTokenBalance, isPending: fetchingTokenBalance } =
    useTokenBalanceAsync();
  const { mutateAsync: fetchBalance, isPending: fetchingBalance } =
    useBalanceAsync();

  const { mutateAsync: transferAsync, isPending: isTransferring } =
    useTransferAsync();

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const formData = new FormData(e.target as HTMLFormElement);
    const type = formData.get("type");
    const token = formData.get("token");
    const amount = formData.get("amount");
    const recepient = formData.get("recepient");

    if (type === "native") {
      await transferAsync({
        address: recepient as string,
        value: parseUnits(amount as string, nativeDecimals),
      });
    } else {
      throw new Error("Not implemented yet");
    }
  }

  const handleMaxAmount = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (amountRef.current) {
      let fee = 0n;
      const provider = getProvider(
        chainId,
        chains,
        wagmiConfig,
        address !== ""
      );
      if (network === NetworkSymbols.eth) {
        const baseFee = 11n;
        const estimatedGasLimit = 21000n;
        if (
          !provider ||
          !(
            provider instanceof JsonRpcProvider ||
            provider instanceof FallbackProvider
          )
        ) {
          throw new Error("Provider not found");
        }
        const gasPrice = (await provider.getFeeData()).gasPrice;
        console.log({ gasPrice });
        if (!gasPrice) {
          throw new Error("Failed to get gas price");
        }
        fee = baseFee * gasPrice * estimatedGasLimit;
      } else if (network === NetworkSymbols.sol) {
        fee = 5000n;
      } else {
        throw new Error("Invalid network");
      }

      if (type === "token") {
        const tokenBalance = await fetchTokenBalance(
          tokenRef.current?.value as string
        );
        amountRef.current.value = formatUnits(tokenBalance, nativeDecimals);
      } else {
        const balance = await fetchBalance();
        amountRef.current.value = Number(
          formatUnits(balance <= fee ? 0n : balance - fee, nativeDecimals)
        ).toString();
      }
    }
  };

  return (
    <form
      className="web3kit-flex web3kit-flex-col web3kit-justify-between web3kit-items-center web3kit-gap-y-4"
      onSubmit={submit}
    >
      <motion.select
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="web3kit-bg-background web3kit-rounded-md web3kit-border web3kit-border-input web3kit-text-sm web3kit-ring-offset-background web3kit-focus:web3kit-ring-ring web3kit-focus:web3kit-ring-offset-2 web3kit-disabled:web3kit-cursor-not-allowed web3kit-disabled:web3kit-opacity-50 web3kit-px-4 web3kit-py-2 web3kit-w-full"
        name="type"
        onChange={(e) => setType(e.target.value)}
      >
        <motion.option value="native" className="web3kit-text-sm">
          {nativeSymbol}
        </motion.option>
        <motion.option value="token" className="web3kit-text-sm">
          Token
        </motion.option>
      </motion.select>

      {type === "token" && (
        <motion.input
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="web3kit-bg-background web3kit-rounded-md web3kit-border web3kit-border-input web3kit-text-sm web3kit-ring-offset-background web3kit-focus:web3kit-ring-ring web3kit-focus:web3kit-ring-offset-2 web3kit-disabled:web3kit-cursor-not-allowed web3kit-disabled:web3kit-opacity-50 web3kit-px-4 web3kit-py-2 web3kit-w-full"
          name="token"
          placeholder="Token Address"
          ref={tokenRef}
        />
      )}

      <div className="web3kit-relative web3kit-w-full">
        <motion.input
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="web3kit-bg-background web3kit-rounded-md web3kit-border web3kit-border-input web3kit-text-sm web3kit-ring-offset-background web3kit-focus:web3kit-ring-ring web3kit-focus:web3kit-ring-offset-2 web3kit-disabled:web3kit-cursor-not-allowed web3kit-disabled:web3kit-opacity-50 web3kit-px-4 web3kit-py-2 web3kit-w-full"
          name="amount"
          placeholder="Amount"
          ref={amountRef}
        />
        <motion.button
          className="web3kit-absolute web3kit-right-2 web3kit-top-1/2 web3kit-text-xs web3kit-text-muted-foreground web3kit-font-medium web3kit-bg-background web3kit-rounded-md web3kit-px-2 web3kit-py-1 web3kit-translate-y-[-50%]"
          onClick={handleMaxAmount}
          disabled={fetchingTokenBalance || fetchingBalance}
        >
          {fetchingTokenBalance || fetchingBalance ? (
            <Spinner className="web3kit-size-6 web3kit-animate-spin" />
          ) : (
            "MAX"
          )}
        </motion.button>
      </div>

      <motion.input
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="web3kit-bg-background web3kit-rounded-md web3kit-border web3kit-border-input web3kit-text-sm web3kit-ring-offset-background web3kit-focus:web3kit-ring-ring web3kit-focus:web3kit-ring-offset-2 web3kit-disabled:web3kit-cursor-not-allowed web3kit-disabled:web3kit-opacity-50 web3kit-px-4 web3kit-py-2 web3kit-w-full"
        name="recepient"
        placeholder="Recepient Address"
      />

      <OptionButton disabled={isTransferring} logo={WithdrawLogo}>
        {isTransferring ? "Withdrawing" : "Withdraw"}
      </OptionButton>
    </form>
  );
}
